import { authenticateAndSetDetails } from "@/api/auth.service";
import ConfirmationDialog from "@/dialogs/ConfirmationDialog.vue";
import SessionExpiringSoonDialog from "@/dialogs/SessionExpiringSoonDialog.vue";
import { routeIsFullyAuthenticated, routeIsRedirectable } from "@/router";
import { useDialogStore } from "@/stores/dialog";
import { useSnackbarStore } from "@/stores/snackbar";
import { useUserStore } from "@/stores/user";

import { markRaw } from "vue";

const AUTH_STATUS = {
  SIGNED_IN: true,
  SIGNED_IN_NOT_VERIFIED: null,
  SIGNED_OUT: false
};

let authStatusInterval = null;

export const checkAuthError = () => {
  if (!document) return;
  const snackbar = useSnackbarStore();
  const cookies = document.cookie.split(";").reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split("=");
    acc[key] = decodeURIComponent(value);
    return acc;
  }, {});

  if (cookies.auth_error) {
    snackbar.showErrorSnackbar({
      message: decodeURIComponent(cookies.auth_error),
      timeout: 20000
    });
    document.cookie =
      "auth_error=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  }
};

export const useAuthEvents = router => {
  if (import.meta.env.VITE_COMPONENT_TESTING) return;

  let signOutHandle;
  let warnHandle;

  const user = useUserStore();
  const dialog = useDialogStore();

  let bc = new BroadcastChannel("auth_status");
  bc.onmessage = e => {
    switch (e.data.event) {
      case "auth_status": {
        const eventData = getSessionData(e.data?.data);
        handleAuthStatus(eventData.authStatus);
        break;
      }
      case "dismiss_warning": {
        if (warnHandle) warnHandle.close();
        break;
      }
    }
  };

  async function handleAuthStatus(status) {
    if (status === true) await signInEvent();
    else if (status === false) await signOutEvent();
  }

  // if a different tab informs this tab that it has authenticated, we will reload
  async function signInEvent() {
    if (signOutHandle) signOutHandle.close();
    if (warnHandle) warnHandle.close();

    // this guard prevents keeping the session infinitely alive
    if (user.id) return;
    try {
      await authenticateAndSetDetails();
    } catch (e) {
      clearSessionExpirationCookie();
      return;
    }
    if (!routeIsFullyAuthenticated(router.currentRoute.value)) {
      const redirectTo = router.currentRoute.value.query?.redirect;
      if (redirectTo && redirectTo !== router.currentRoute.value.fullPath) {
        location.replace(redirectTo);
      } else {
        location.reload();
      }
    }
    signOutHandle = null;
  }

  async function signOutEvent() {
    if (!user.id) return;

    await user.logoutUser(
      router,
      routeIsRedirectable(router.currentRoute.value)
    );

    if (warnHandle) warnHandle.close();
    if (signOutHandle) return;

    signOutHandle = dialog.showDialog({
      component: markRaw(ConfirmationDialog),
      returnHandle: true,
      title: "You have been signed out",
      subtitle:
        "You've been signed out of BOSS. BOSS sessions in all tabs have been signed out.",
      confirmText: "Close",
      hideCancel: true
    });
  }

  async function handleExpiringSoon(ttl) {
    if (!user.id) return;
    if (signOutHandle) return;
    const expiringSoon = ttl < 60 * 15;
    if (warnHandle) {
      warnHandle.close();
      warnHandle = null;
    }

    if (expiringSoon) {
      warnHandle = dialog.showDialog({
        component: markRaw(SessionExpiringSoonDialog),
        returnHandle: true,
        timeInSeconds: ttl,
        stillPresentFunc: async () => {
          try {
            await authenticateAndSetDetails();
            bc.postMessage({ event: "dismiss_warning", data: true });
          } catch (e) {
            return;
          }
        },
        expiredFunc: signOutEvent
      });
    }
  }

  function getSessionExpirationCookieData() {
    const sessionDataCookie = document.cookie
      .split("; ")
      .find(x => x.startsWith("fwsession_expiration"));

    let sessionCookieData = null;
    if (sessionDataCookie) {
      sessionCookieData = JSON.parse(
        decodeURIComponent(sessionDataCookie.split("=")[1])
      );
    }

    return sessionCookieData;
  }

  function clearSessionExpirationCookie() {
    document.cookie =
      "fwsession_expiration=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;" +
      (import.meta.env.PROD ? "; Secure" : "");
  }

  function getSessionData(value) {
    let authStatus = AUTH_STATUS.SIGNED_OUT;
    let sessionExpiration = 0;
    let serverNow = 0;

    if (value) {
      sessionExpiration = value.expires_on;
      serverNow = value.server_now;

      authStatus = value.otp_verified
        ? AUTH_STATUS.SIGNED_IN
        : AUTH_STATUS.SIGNED_IN_NOT_VERIFIED;
    }

    return {
      authStatus,
      expiryUtc: sessionExpiration,
      serverNowUtc: serverNow
    };
  }

  function toMs(t) {
    if (typeof t === "number") return t;
    const ms = Date.parse(t);
    if (Number.isNaN(ms)) throw new Error("Bad time format");
    return ms;
  }

  let previousServerNowUTC;
  let skewMs;

  function secondsLeft({ expiryUtc, serverNowUtc }) {
    const expiryMs = toMs(expiryUtc);

    const clientNowAtReceipt = Date.now();

    // include the offset which is only updated as the server now changes
    if (serverNowUtc != previousServerNowUTC) {
      previousServerNowUTC = serverNowUtc;
      const serverNowMs = toMs(serverNowUtc);
      skewMs = serverNowMs - clientNowAtReceipt;
    }

    // Use a monotonic base to avoid user changing system clock
    const baseMono = performance.now();
    const baseClientNow = clientNowAtReceipt;

    // current client time ≈ baseClientNow + (performance.now() - baseMono)
    const clientNow = baseClientNow + (performance.now() - baseMono);
    const correctedNow = clientNow + skewMs;
    return Math.floor(Math.max(0, expiryMs - correctedNow) * 0.001);
  }

  const cookieData = getSessionExpirationCookieData();
  const initialData = getSessionData(cookieData);
  handleAuthStatus(initialData.authStatus);
  bc.postMessage({
    event: "auth_status",
    data: getSessionExpirationCookieData()
  });

  if (authStatusInterval) clearInterval(authStatusInterval);
  authStatusInterval = setInterval(() => {
    const intervalData = getSessionData(getSessionExpirationCookieData());

    if (intervalData.authStatus !== AUTH_STATUS.SIGNED_IN_NOT_VERIFIED) {
      handleAuthStatus(intervalData.authStatus);
    }

    const time = secondsLeft({
      expiryUtc: intervalData.expiryUtc,
      serverNowUtc: intervalData.serverNowUtc
    });

    handleExpiringSoon(time);
    if (time <= 0 && user.id) {
      signOutEvent();
    }
  }, 1000);

  function stopInterval() {
    if (authStatusInterval) clearInterval(authStatusInterval);
  }

  return { stopInterval };
};
